import { useCallback, useEffect, useRef } from "react";
import { useWatch } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";

import { useIntl } from "react-intl";
import { applyZipcodeLookup, isAbortError } from "helpers/zipcodeGeocode";
import { fetchCitiesQuery, useGetCities } from "api/queries/common/useGetCities";
import { useGetCountries } from "api/queries/common/useGetCountries";
import { fetchStatesV2Query, useGetStatesV2 } from "api/queries/common/useGetStates";
import {
  STATE_MANDATORY_COUNTRIES,
  COUNTRIES_WITH_STATES_OR_PROVINCES,
  CITY_SELECT_STATE_AUTOFILL_COUNTRIES,
} from "constants/AddressContants";
import Input from "components/atoms/Input/Input";
import ZipcodeLookupSpinner from "components/atoms/ZipcodeLookupSpinner/ZipcodeLookupSpinner";
import useDelayedLoadingIndicator from "hooks/useDelayedLoadingIndicator";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";

type AddressV2FieldTypes = {
  countryId: string;
  cityId: string;
  stateId: string;
  stateName: string;
  cityName: string;
  countryName: string;
  zipcode?: string;
};

const FIELD_NAME: AddressV2FieldTypes = {
  countryId: "country_id",
  cityId: "city_id",
  stateId: "state_id",
  stateName: "state_name",
  cityName: "city_name",
  countryName: "country_name",
  zipcode: "zipcode",
};

interface useAddressV2FieldsProps {
  viewOnly?: boolean;
  /* eslint-disable @typescript-eslint/no-explicit-any -- reused across forms with different RHF generics */
  control: any;
  watch: any;
  setValue: any;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  enable_new_address_structure?: boolean;
  useV2?: boolean;
  fieldNames?: AddressV2FieldTypes;
  allowCustomCity?: boolean;
  customCityFieldName?: string;
  /** Opt in to geocoding the postal code to auto-fill state and city on zip blur. */
  enableZipcodeLookup?: boolean;
}

const ZIPCODE_LOOKUP_TIMEOUT_MS = 10000;

export default function useAddressV2Fields({
  viewOnly,
  control,
  watch,
  setValue,
  enable_new_address_structure,
  useV2 = false,
  fieldNames = FIELD_NAME,
  allowCustomCity = false,
  customCityFieldName = "custom_city_name",
  enableZipcodeLookup = false,
}: useAddressV2FieldsProps) {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const lastZipcodeLookupKeyRef = useRef<string | null>(null);
  const zipcodeLookupAbortRef = useRef<AbortController | null>(null);
  const { showIndicator: showZipcodeLookupIndicator, runWithLoading: runZipcodeLookupWithLoading } =
    useDelayedLoadingIndicator();

  useEffect(() => () => zipcodeLookupAbortRef.current?.abort(), []);

  const [countryId, stateId, cityId] = useWatch({
    control,
    name: [fieldNames.countryId, fieldNames.stateId, fieldNames.cityId],
  });

  const { data: countries = [], isInitialLoading: isCountriesFetching } = useGetCountries({
    useV2,
    enabled: enable_new_address_structure,
    select: (data: any) =>
      data.map(({ label, id, value, code }: { label: string; id: number; value: string; code: string }) => ({
        label,
        value: id,
        code: value,
        googleCountryCode: code,
      })),
  });

  const countryCode = enable_new_address_structure
    ? countries?.find((c: any) => c.value.toString() === countryId?.toString())?.code
    : null;
  const isStateMandatory = STATE_MANDATORY_COUNTRIES.includes(countryCode);

  const { data: states = [], isInitialLoading: isStatesFetching } = useGetStatesV2(countryId!, {
    select: (data) => data.map(({ name, id }) => ({ label: name, value: id })),
    enabled: !!countryId && enable_new_address_structure,
  });

  const countryISOCode = countries.find((c: any) => c.value?.toString() === countryId?.toString())?.code;
  const disableStateSelect = !countryId || isStatesFetching;
  const { data: cities = [], isInitialLoading: isCitiesFetching } = useGetCities(
    { country: countryId!, ...(stateId && (isStateMandatory || !cityId) && { state: stateId }) },
    {
      select: (data) =>
        data.map(({ name, name_wo_diacritics, id, ...rest }) => ({ ...rest, label: name, value: id, name_wo_diacritics })),
      enabled: !!countryId && (!isStateMandatory || !!stateId) && enable_new_address_structure,
    },
  );

  const cityIdInCitiesOptions = cities?.find((city) => city.value === cityId);
  const citiesOptionsWithCustomCity =
    typeof cityId !== "string" && cityId && !cityIdInCitiesOptions
      ? [
          {
            label: String(watch(fieldNames.cityName)),
            name_wo_diacritics: String(watch(fieldNames.cityName)),
            state_id: Number(stateId),
            value: Number(cityId),
          },
          ...cities,
        ]
      : cities;

  const statesOptions =
    stateId && !states?.find((state) => state.value?.toString() === stateId?.toString())
      ? [
          {
            value: stateId,
            label: String(watch(fieldNames.stateName) ?? stateId),
          },
          ...states,
        ]
      : states;

  const onZipcodeBlur = useCallback(
    async (
      overrides?: { countryId?: number | string; countryName?: string; googleCountryCode?: string },
      zipcodeOverride?: string,
    ): Promise<boolean> => {
      const zipcodeFieldName = fieldNames.zipcode;
      // Consumers may supply fieldNames without a zipcode entry; watch(undefined) would hand back
      // the whole form object rather than a value.
      if (!enable_new_address_structure || !enableZipcodeLookup || !zipcodeFieldName) {
        return false;
      }

      const rawZipcode = zipcodeOverride ?? watch(zipcodeFieldName);
      const zipcode = typeof rawZipcode === "string" ? rawZipcode.trim() : "";
      const resolvedCountryId = overrides?.countryId ?? countryId;
      const selectedCountry = countries.find((c: any) => c.value?.toString() === resolvedCountryId?.toString());
      const resolvedCountryName = overrides?.countryName || watch(fieldNames.countryName) || selectedCountry?.label || "";
      const resolvedCountryIsoCode = selectedCountry?.code;
      const resolvedGoogleCountryCode = overrides?.googleCountryCode || selectedCountry?.googleCountryCode;
      const resolvedCountryHasStatesOrProvinces =
        COUNTRIES_WITH_STATES_OR_PROVINCES.indexOf(resolvedCountryIsoCode ?? "") !== -1;
      const currentStateId = watch(fieldNames.stateId);
      const currentCityId = watch(fieldNames.cityId);
      const currentCityName = watch(fieldNames.cityName);
      const currentCustomCityName = watch(customCityFieldName);

      const isStateEmpty = !currentStateId;
      const isCityEmpty = !currentCityId && !currentCustomCityName && !currentCityName;
      const lookupKey = `${resolvedCountryId}:${zipcode}`;
      const shouldAutoFill = isStateEmpty && isCityEmpty;
      const shouldReLookup = lastZipcodeLookupKeyRef.current !== null && lookupKey !== lastZipcodeLookupKeyRef.current;

      if (!zipcode || !resolvedCountryId || !resolvedCountryName) {
        return false;
      }

      if (!shouldAutoFill && !shouldReLookup) {
        return false;
      }

      // A newer lookup always wins, so a slow response for a previous zip or country can never
      // overwrite the current selection.
      zipcodeLookupAbortRef.current?.abort();
      const abortController = new AbortController();
      zipcodeLookupAbortRef.current = abortController;
      const timeoutId = setTimeout(() => abortController.abort(), ZIPCODE_LOOKUP_TIMEOUT_MS);

      try {
        const applied = await runZipcodeLookupWithLoading(() =>
          applyZipcodeLookup({
            zipcode,
            countryName: resolvedCountryName,
            googleCountryCode: resolvedGoogleCountryCode,
            countryHasStatesOrProvinces: resolvedCountryHasStatesOrProvinces,
            customCityFieldName,
            allowCustomCity,
            setValue,
            fieldNames: {
              stateId: fieldNames.stateId,
              stateName: fieldNames.stateName,
              cityId: fieldNames.cityId,
              cityName: fieldNames.cityName,
            },
            fetchStates: async () => {
              const states = await fetchStatesV2Query(queryClient, Number(resolvedCountryId));

              return states.map(({ name, id, code }) => ({ label: name, value: id, code }));
            },
            fetchCities: (stateIdForCities) =>
              fetchCitiesQuery(queryClient, {
                country: Number(resolvedCountryId),
                ...(stateIdForCities && { state: stateIdForCities }),
              }),
            signal: abortController.signal,
          }),
        );

        // Only remember the lookup once it actually filled something in. Otherwise a zip with no
        // match would arm the re-lookup path and later overwrite manually entered state/city.
        if (applied) {
          lastZipcodeLookupKeyRef.current = lookupKey;
        }

        return applied;
      } catch (error) {
        // A failed lookup leaves state/city as manual entry, but anything other than an abort is
        // worth surfacing rather than swallowing.
        if (!isAbortError(error)) {
          console.error("Postal code city/state lookup failed", error);
        }

        return false;
      } finally {
        clearTimeout(timeoutId);
        if (zipcodeLookupAbortRef.current === abortController) {
          zipcodeLookupAbortRef.current = null;
        }
      }
    },
    [
      allowCustomCity,
      countries,
      countryId,
      customCityFieldName,
      enable_new_address_structure,
      enableZipcodeLookup,
      fieldNames.cityId,
      fieldNames.cityName,
      fieldNames.countryName,
      fieldNames.stateId,
      fieldNames.stateName,
      fieldNames.zipcode,
      queryClient,
      setValue,
      watch,
      runZipcodeLookupWithLoading,
    ],
  );

  if (!enable_new_address_structure)
    return {
      fields: [],
      countries: [],
      countryId: null,
      countryCode: null,
      stateId: null,
      statesOptions: [],
      citiesOptions: [],
      isCountriesFetching: false,
      isStatesFetching: false,
      isCitiesFetching: false,
      isStateMandatory: false,
      disableStateSelect: true,
      /* eslint-disable @typescript-eslint/no-empty-function */
      onSelectCountry: () => {},
      onSelectState: () => {},
      onSelectCity: () => {},
      /* eslint-enable @typescript-eslint/no-empty-function */
      onZipcodeBlur: async () => false,
      showZipcodeLookupIndicator: false,
    };

  const countryOptions =
    countries.length === 0 && countryId
      ? [
          {
            value: countryId,
            label: String(watch(fieldNames.countryName) ?? countryId),
          },
        ]
      : countries;

  const citiesOptions =
    cities.length === 0 && cityId
      ? [
          {
            value: cityId,
            label: String(watch(fieldNames.cityName) ?? cityId),
          },
        ]
      : citiesOptionsWithCustomCity;

  const onSelectCountry = (event: any) => {
    lastZipcodeLookupKeyRef.current = null;
    setValue(fieldNames.stateId, null);
    setValue(fieldNames.cityId, null);
    setValue(fieldNames.stateName, "");
    setValue(fieldNames.cityName, "");
    // Only forms that opted into custom cities have this field; writing it elsewhere would add a
    // stray key to the submitted payload.
    if (allowCustomCity) {
      setValue(customCityFieldName, null);
    }
    const countryLabel = event?.[0]?.label || "";
    setValue(fieldNames.countryName, countryLabel);
    onZipcodeBlur({
      countryId: event?.[0]?.value,
      countryName: countryLabel,
      googleCountryCode: event?.[0]?.googleCountryCode,
    });
  };

  const onSelectState = (event: any) => {
    setValue(fieldNames.cityId, null);
    setValue(fieldNames.stateName, event?.[0]?.label || "");
  };

  const onSelectCity = (event: any) => {
    const nextStateId = event?.[0]?.state_id;
    setValue(fieldNames.cityName, event?.[0]?.label || "");
    if (!nextStateId) return;
    if (countryISOCode && CITY_SELECT_STATE_AUTOFILL_COUNTRIES.includes(countryISOCode)) {
      setValue(fieldNames.stateId, nextStateId);
    }
  };

  const countryLabel = intl.formatMessage({ id: "general.country", defaultMessage: "Country" });
  const stateLabelElement = (
    <span className={disableStateSelect ? "text-secondary" : ""}>
      {intl.formatMessage({ id: "new-request.schedule.stateOrProvince", defaultMessage: "State / Province" })}
    </span>
  );
  const cityLabel = intl.formatMessage({ id: "general.city", defaultMessage: "City" });
  const zipcodeLabel = intl.formatMessage({
    id: "settings.address-book.zipOrPostalCode",
    defaultMessage: "Zip / Postal code",
  });

  const countryField = (
    <SearchSelect
      required
      placeholder={intl.formatMessage({
        id: "general.select",
        defaultMessage: "Select",
      })}
      name={fieldNames.countryId}
      options={countryOptions}
      onSelect={onSelectCountry}
      isLoading={isCountriesFetching}
      disable={isCountriesFetching || viewOnly}
    />
  );

  const stateField = (
    <SearchSelect
      required={isStateMandatory}
      placeholder={
        disableStateSelect
          ? ""
          : intl.formatMessage({
              id: "general.select",
              defaultMessage: "Select",
            })
      }
      name={fieldNames.stateId}
      options={statesOptions}
      onSelect={onSelectState}
      disable={disableStateSelect || viewOnly}
      isLoading={isStatesFetching}
    />
  );

  const cityField = (
    <SearchSelect
      required
      placeholder={intl.formatMessage({
        id: "general.select",
        defaultMessage: "Select",
      })}
      name={fieldNames.cityId}
      options={citiesOptions}
      onSelect={onSelectCity}
      disable={
        !watch(fieldNames.countryId) || (isStateMandatory && !watch(fieldNames.stateId)) || isCitiesFetching || viewOnly
      }
      isLoading={isCitiesFetching}
      filterBy={["label", "name_wo_diacritics"]}
    />
  );

  const zipcodeField = enableZipcodeLookup ? (
    <div className="d-flex align-items-center">
      <div className="flex-grow-1">
        <Input
          type="text"
          name={fieldNames.zipcode}
          className="rounded-input input-width-lg"
          disabled={viewOnly}
          onBlur={(event) => {
            onZipcodeBlur(undefined, event.currentTarget.value);
          }}
        />
      </div>
      <ZipcodeLookupSpinner visible={showZipcodeLookupIndicator} />
    </div>
  ) : (
    <Input type="text" name={fieldNames.zipcode} className="rounded-input input-width-lg" disabled={viewOnly} />
  );

  const fields = [
    {
      label: countryLabel,
      children: countryField,
      required: true,
    },
    {
      label: stateLabelElement,
      children: stateField,
      required: isStateMandatory,
    },
    {
      label: cityLabel,
      children: cityField,
      required: true,
    },
    {
      label: zipcodeLabel,
      children: zipcodeField,
      required: false,
    },
  ];

  return {
    fields,
    countries,
    countryId,
    countryCode,
    stateId,
    statesOptions,
    citiesOptions,
    isCountriesFetching,
    isStatesFetching,
    isCitiesFetching,
    isStateMandatory,
    disableStateSelect,
    onSelectCountry,
    onSelectState,
    onSelectCity,
    onZipcodeBlur,
    showZipcodeLookupIndicator,
  };
}
